// Unpublished Work © 2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import FormDialog from 'Ui/components/common/form-dialog/form-dialog';
import FormValidator from 'Ui/components/higher-order-components/form-validator';
import {closeDialog as closeReduxDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {postInventoryTransfers, getPartsFromInventoryBins, getBinLocations, updatePartBin} from 'Services/inventory-service';
import {useSave} from 'Ui/react-hooks/use-save';
import SelectedPartQuantityWithCost from 'Ui/components/common/parts-selectors/selected-part-quantity-with-cost';
import PartSelector from 'Ui/components/common/parts-selectors/part-selector';
import InventoryPartsTotalCost from 'Ui/features/onequip/parts/inventory/inventory-parts-total-cost';
import {asNumber} from 'Utils/conversion-utils';
import {fetchEffectData, useDeepEffect} from 'Utils/react-utils';
import {alphaNumericCompare} from 'Ui/models/maintenance';
import LoadingWrapper from 'Ui/components/common/loading-wrapper';
import {setInventoryParts as setReduxInventoryParts} from 'Store/actions/equipment';
import {simpleRoundValue} from 'Common/utils/units/unit-converter';

// Gets the latest partBins in the inventory and sets it in the global state
async function getLatestPartsFromInventoryBins(inventories, setInventoryParts) {
    const inventoryParts = await getPartsFromInventoryBins(inventories);

    setInventoryParts(inventoryParts);
    return inventoryParts;
}

// Update the binLocation of the modified parts
async function updatePartBinsLocation(partUpdates, inventories, inventoryParts, setInventoryParts) {
    if (!partUpdates.length) {
        return;
    }
    let currentInventoryParts = inventoryParts;

    for (const partToUpdate of partUpdates) {
        let partBin = currentInventoryParts.find((pBin) => pBin.partId === partToUpdate.partId);

        if (!partBin) {
            currentInventoryParts = await getLatestPartsFromInventoryBins(inventories, setInventoryParts);
            partBin = currentInventoryParts.find((pBin) => pBin.partId === partToUpdate.partId);
        }
        await updatePartBin(partBin.partBinId, partToUpdate);
    }
}

function StockInventoryDialog(props) {
    const {
        availableParts,
        closeDialog,
        currencyPreference,
        initialSelectedPart,
        invalidInputs,
        inventories,
        onClose,
        setValid,
        translations,
        inventoryParts = [],
        setInventoryParts
    } = props;

    const [loading, setLoading] = React.useState(false);
    const [binLocations, setBinLocations] = React.useState([]);

    async function fetchBinLocations(isMounted = () => true) {
        setLoading(true);

        const inventoryId = inventories?.length ? inventories[0].inventoryId : null;
        const {binLocations} = inventoryId ? await getBinLocations(inventoryId) : {
            binLocations: []
        };

        const remappedBinLocations = binLocations.map((binLocation) => {
            return {
                id: binLocation.binLocationId,
                binLocationId: binLocation.binLocationId,
                name: binLocation.name,
                title: binLocation.name
            };
        }).sort((a, b) => alphaNumericCompare(a.title, b.title));

        if (isMounted()) {
            setBinLocations(remappedBinLocations);
            setLoading(false);
        }

        return remappedBinLocations;
    }

    useDeepEffect(() => fetchEffectData(fetchBinLocations), []);

    const [selectedParts, setSelectedParts] = React.useState(initialSelectedPart ? [{
        ...initialSelectedPart,
        id: initialSelectedPart.partId,
        quantity: 0,
        subtitle: `${initialSelectedPart.manufacturerName} - ${initialSelectedPart.name}`,
        title: initialSelectedPart.partNumber,
        // Fallback for legacy part_bin rows where lastUnitCost may be null.
        unitCost: simpleRoundValue(asNumber(initialSelectedPart.lastUnitCost ?? initialSelectedPart.unitCost))
    }] : []);

    const [saveFunc, disableSave, isSaving] = useSave(async () => {
        const partTransfers = selectedParts
            .map(({
                binLocation, binLocationId, prevBinLocationId, ...part
            }) => ({
                ...part,
                quantity: asNumber(part.quantity),
                totalCost: asNumber(part.totalCost),
                unitCost: asNumber(part.unitCost)
            }));

        const transferData = {
            destinationInventoryId: inventories[0].inventoryId,
            partTransfers
        };

        await postInventoryTransfers(transferData);

        // Update the partBins if the binLocation was changed
        const partUpdates = selectedParts
            .filter((part) => !part.prevBinLocationId || part.prevBinLocationId !== part.binLocationId)
            .map((p) => ({
                binLocationId: p.binLocationId,
                partId: p.partId
            }));

        try {
            await updatePartBinsLocation(partUpdates, inventories, inventoryParts, setInventoryParts);
        } catch (error) {
            throw error;
        }

        if (onClose) {
            onClose();
        }

        closeDialog();
    }, {
        invalidInputs
    });

    const initializedAvailableParts = React.useMemo(() => availableParts.map((part) => ({
        ...part,
        quantity: 0,
        unitCost: 0
    })), [availableParts]);

    return (
        <FormDialog
            cancelLabel={translations.CANCEL}
            className='stock-inventory-dialog dropdown-overflow-dialog'
            closeHandler={closeDialog}
            disableSave={disableSave || selectedParts.length === 0}
            footerLoading={isSaving}
            onSave={saveFunc}
            saveLabel={translations.ONLINK_STOCK_INVENTORY}
            title={translations.ONLINK_STOCK_INVENTORY}
            translations={translations}
            width={'md'}
        >
            <LoadingWrapper
                className='table-loading-icon'
                loading={loading}
                size='50px'
            >
                <form className='stock-inventory-form'>
                    <PartSelector
                        additionalSubTitleSelector='partNumber'
                        availableParts={initializedAvailableParts}
                        binLocations={binLocations}
                        currencyPreference={currencyPreference}
                        disabled={false}
                        fetchBinLocations={fetchBinLocations}
                        inventories={inventories}
                        inventoryParts={inventoryParts}
                        partsInputComponent={SelectedPartQuantityWithCost}
                        selectedParts={selectedParts}
                        selectedPartsClassName={'selected-parts'}
                        setSelectedParts={setSelectedParts}
                        setValid={setValid}
                        showAddNewPartOption={true}
                        subTitleSelector='manufacturerName'
                        titleSelector='name'
                        translations={translations}
                    />
                </form>
                <InventoryPartsTotalCost
                    currencyPreference={currencyPreference}
                    selectedParts={selectedParts}
                    translations={translations}
                />
            </LoadingWrapper>
        </FormDialog>
    );
}

StockInventoryDialog.propTypes = {
    availableParts: PropTypes.arrayOf(PropTypes.object),
    closeDialog: PropTypes.func,
    currencyPreference: PropTypes.string,
    initialSelectedPart: PropTypes.object,
    invalidInputs: PropTypes.instanceOf(Set),
    inventories: PropTypes.arrayOf(PropTypes.object),
    inventoryParts: PropTypes.arrayOf(PropTypes.object),
    onClose: PropTypes.func,
    setInventoryParts: PropTypes.func,
    setValid: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        translations: state.translations,
        currencyPreference: state.membership.currencyPreference,
        inventoryParts: state.equipment.inventoryParts
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeDialog() {
            dispatch(closeReduxDialog(dialogTypes.STOCK_INVENTORY_DIALOG));
        },
        setInventoryParts(value) {
            dispatch(setReduxInventoryParts(value));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(FormValidator(StockInventoryDialog));
